/**
 * Virtual list component for performance optimization
 * Efficiently renders large task lists by only rendering visible items
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

export interface VirtualListProps<T> {
  items: T[];
  height: number;
  itemHeight: number | ((index: number) => number);
  overscan?: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  onScroll?: (scrollTop: number) => void;
  emptyMessage?: string;
}

export function VirtualList<T>({
  items,
  height,
  itemHeight,
  overscan = 5,
  renderItem,
  className = '',
  onScroll,
  emptyMessage = 'No items to display',
}: VirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Calculate item heights
  const getItemHeight = useCallback(
    (index: number) => {
      return typeof itemHeight === 'function' ? itemHeight(index) : itemHeight;
    },
    [itemHeight]
  );

  // Calculate total height
  const totalHeight = items.reduce((acc, _, index) => {
    return acc + getItemHeight(index);
  }, 0);

  // Calculate visible range
  const getVisibleRange = useCallback(() => {
    let accumulatedHeight = 0;
    let startIndex = 0;
    let endIndex = items.length - 1;

    // Find start index
    for (let i = 0; i < items.length; i++) {
      const itemHeight = getItemHeight(i);
      if (accumulatedHeight + itemHeight > scrollTop) {
        startIndex = Math.max(0, i - overscan);
        break;
      }
      accumulatedHeight += itemHeight;
    }

    // Find end index
    accumulatedHeight = 0;
    for (let i = startIndex; i < items.length; i++) {
      if (accumulatedHeight > scrollTop + height) {
        endIndex = Math.min(items.length - 1, i + overscan);
        break;
      }
      accumulatedHeight += getItemHeight(i);
    }

    return { startIndex, endIndex };
  }, [items.length, scrollTop, height, overscan, getItemHeight]);

  // Calculate offset for visible items
  const getItemOffset = useCallback(
    (index: number) => {
      let offset = 0;
      for (let i = 0; i < index; i++) {
        offset += getItemHeight(i);
      }
      return offset;
    },
    [getItemHeight]
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const newScrollTop = e.currentTarget.scrollTop;
      setScrollTop(newScrollTop);
      setIsScrolling(true);
      
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Set scrolling to false after scroll ends
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
      
      onScroll?.(newScrollTop);
    },
    [onScroll]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (items.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-gray-500 dark:text-gray-400 ${className}`}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const { startIndex, endIndex } = getVisibleRange();
  const visibleItems = items.slice(startIndex, endIndex + 1);

  return (
    <div
      ref={containerRef}
      className={`overflow-auto ${className}`}
      style={{ height }}
      onScroll={handleScroll}
    >
      {/* Total height container */}
      <div style={{ height: totalHeight, position: 'relative' }}>
        {/* Visible items */}
        {visibleItems.map((item, index) => {
          const actualIndex = startIndex + index;
          const offset = getItemOffset(actualIndex);
          const itemHeight = getItemHeight(actualIndex);

          return (
            <div
              key={actualIndex}
              style={{
                position: 'absolute',
                top: offset,
                left: 0,
                right: 0,
                height: itemHeight,
                // Reduce opacity while scrolling for better performance
                opacity: isScrolling ? 0.8 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {renderItem(item, actualIndex)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Hook to use with VirtualList for dynamic item heights
 */
export function useVirtualListDynamicHeights<T>(
  items: T[],
  estimatedItemHeight: number = 60
) {
  const heightsRef = useRef<Map<number, number>>(new Map());
  const [, forceUpdate] = useState({});

  const measureItem = useCallback((index: number, element: HTMLElement | null) => {
    if (!element) return;
    
    const height = element.getBoundingClientRect().height;
    const currentHeight = heightsRef.current.get(index);
    
    if (currentHeight !== height) {
      heightsRef.current.set(index, height);
      forceUpdate({});
    }
  }, []);

  const getItemHeight = useCallback(
    (index: number) => {
      return heightsRef.current.get(index) || estimatedItemHeight;
    },
    [estimatedItemHeight]
  );

  const resetHeights = useCallback(() => {
    heightsRef.current.clear();
    forceUpdate({});
  }, []);

  return {
    getItemHeight,
    measureItem,
    resetHeights,
  };
}